/**
 * Reopens the cookie consent preferences from a link anywhere on the page —
 * typically an item in the footer bottom menu.
 *
 * `snippets/footer-design.liquid` renders the merchant's menu links verbatim, so
 * a menu item whose web address is `#show-cookie-banner` reaches the page as a
 * plain `<a href="#show-cookie-banner">` with no target to jump to. This script
 * intercepts that click and hands it to the consent UI.
 *
 * Preference order:
 *   1. `privacyBanner.showPreferences()` — Shopify's own dialog, the same one the
 *      native banner's "Manage preferences" button opens. This is the consent UI
 *      the shop actually uses, so it is the one the link belongs to, and it stays
 *      correct when the banner's wording or categories change in the admin.
 *      Verified on the live shop for a visitor who has already decided: the banner
 *      is gone from the DOM, and the dialog still opens, pre-filled from the
 *      stored decision.
 *   2. `window.MinimogTheme.CookieConsent.open()` — the theme's own panel, for a
 *      page where the native bundle never arrives. It writes through the same
 *      Customer Privacy API, so it is a real fallback rather than a decoration,
 *      and it says so out loud when that API is blocked too.
 *   3. Nothing. The click has already been swallowed, so a `console.warn` is the
 *      only trace left to find.
 *
 * Loaded from `layout/theme.liquid` next to the Consent Mode bridge.
 */
(function () {
  var HASH = '#show-cookie-banner';

  // How long to keep looking for the native banner API after a click, in ms. The
  // bundle is loaded with `async`, so a click can arrive before its global exists.
  // Kept short on purpose: a blocked bundle never produces that global at all, and
  // waiting the full three seconds of the first version only meant three seconds
  // of a dead link before the fallback ran.
  var WAIT_MS = 1500;

  function nativeBanner() {
    var banner = window.privacyBanner;
    if (banner && typeof banner.showPreferences === 'function') return banner;
    return null;
  }

  function themeBanner() {
    var api = window.MinimogTheme && window.MinimogTheme.CookieConsent;
    if (api && typeof api.open === 'function') return api;
    return null;
  }

  function openTheme() {
    var api = themeBanner();
    if (!api) return false;

    api.open();
    return true;
  }

  function openNative() {
    var banner = nativeBanner();
    if (!banner) return false;

    try {
      var opened = banner.showPreferences();

      // Documented as a promise, but guard the chain: an older build returning
      // undefined would otherwise throw and take the whole handler down.
      if (opened && typeof opened.catch === 'function') {
        opened.catch(function (error) {
          console.error('[cookie-preferences] showPreferences failed', error);
          openTheme();
        });
      }
    } catch (error) {
      console.error('[cookie-preferences] showPreferences threw', error);
      return openTheme();
    }

    return true;
  }

  function openWhenReady(deadline) {
    if (openNative()) return;

    if (Date.now() >= deadline) {
      if (openTheme()) return;

      // Neither UI answered, and the click has already been swallowed. Say so:
      // without a line here a misconfigured store shows a link that does nothing
      // at all, with no way to tell which half is missing.
      console.warn(
        '[cookie-preferences] no preferences API after ' + WAIT_MS + 'ms. privacyBanner: ' +
          (window.privacyBanner ? 'present' : 'missing') +
          ', showPreferences: ' +
          (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function'
            ? 'present'
            : 'missing') +
          ', MinimogTheme.CookieConsent: ' +
          (themeBanner() ? 'present' : 'missing')
      );
      return;
    }

    window.setTimeout(function () {
      openWhenReady(deadline);
    }, 100);
  }

  function matches(anchor) {
    var hash;

    try {
      hash = new URL(anchor.href, window.location.href).hash;
    } catch (error) {
      return false;
    }

    return hash.toLowerCase() === HASH;
  }

  // The link performs an action rather than navigating, so tell assistive tech
  // what it opens. The accessible name stays the merchant's menu item title.
  function decorate() {
    document.querySelectorAll('a[href*="' + HASH + '"]').forEach(function (anchor) {
      if (matches(anchor)) anchor.setAttribute('aria-haspopup', 'dialog');
    });
  }

  document.addEventListener('click', function (event) {
    // A middle-click, a Cmd-click or a Shift-click is a request for a new tab or
    // window, not for the modal. Hand those back to the browser untouched.
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var target = event.target;
    if (!(target instanceof Element)) return;

    var anchor = target.closest('a[href]');
    if (!anchor || !matches(anchor)) return;

    // Swallow the navigation unconditionally. `#show-cookie-banner` has no
    // target on the page, so letting it through only rewrites the URL and makes
    // the link look broken - which is what the first live version did.
    event.preventDefault();
    openWhenReady(Date.now() + WAIT_MS);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', decorate);
  } else {
    decorate();
  }
})();
